import dayjs from "dayjs"
import { useEffect, useState } from "react"
import {
  NudgeTakeFragment,
  PractitionerFragment,
  useGetPractitionerDataQuery,
} from "../generated/graphql"
import { NudgeDomain } from "../utils/constants"
import {
  ClientProgressData,
  StaffOverviewData,
  StaffTableData,
  TechniqueProfileData,
} from "../utils/types"
import { useGraphQLClient } from "./useGraphQLClient"
import {
  getClientCountBeforeMonth,
  getHighScore,
  getMonthPercentages,
  iterateProgress,
  useOrganization,
} from "./useOrganization"

/**
 * Builds practitioner detail overview counts directly from the staff-table row.
 */
export function getOverview(staffTableData: StaffTableData[], id: string) {
  const practitioner = staffTableData.find((item) => item.id.data === id)
  if (!practitioner) {
    return
  }

  const viraClients = Number(practitioner.viraClients.data) || 0
  const nudges = Number(practitioner.nudges.data) || 0
  const questionnaires = Number(practitioner.questionnaires.data) || 0

  const overview = {
    viraClients,
    nudges,
    questionnaires,
    casemix: {
      ...practitioner.casemix.data,
    },
  }
  return overview
}

function getTechniqueProfile(practitioner: PractitionerFragment) {
  let allNudgeTakes: NudgeTakeFragment[] = []
  practitioner.practitioner_invite_codes.forEach((code) => {
    const user = code.user
    if (!user) {
      return
    }
    const months = 6
    const sixMonthsAgo = dayjs().subtract(months, "months")
    const nudgeTakes = user.nudge_takes.filter(
      (take) =>
        take.completed_at && dayjs(take.completed_at).isAfter(sixMonthsAgo)
    )
    allNudgeTakes = [...allNudgeTakes, ...nudgeTakes]
  })
  const nudgeTakesByType = new Map<string, NudgeTakeFragment[]>()
  allNudgeTakes.forEach((take) => {
    const name = take.nudge_schedule.nudge.technique
    const takes = nudgeTakesByType.get(name) ?? []
    takes.push(take)
    nudgeTakesByType.set(name, takes)
  })
  const nudgeTakes = Array.from(nudgeTakesByType.entries()).sort((a, b) =>
    a[1].length < b[1].length ? 1 : a[1].length > b[1].length ? -1 : 0
  )
  const newTechniqueProfileData: TechniqueProfileData = {
    topTenTechniques: Array.from(nudgeTakesByType.keys())
      .map((label) => {
        const takes = nudgeTakesByType.get(label) ?? []
        return {
          label,
          domain: takes[0].nudge_schedule.nudge.domain as NudgeDomain,
          percent: takes.length / allNudgeTakes.length,
          active: takes[0].nudge_schedule.nudge.active,
        }
      })
      .sort((a, b) =>
        a.percent < b.percent ? 1 : a.percent > b.percent ? -1 : 0
      ),
    mostUsed: nudgeTakes.slice(0, 3).map((take) => ({
      label: take[0],
      domain: take[1][0].nudge_schedule.nudge.domain as NudgeDomain,
      count: take[1].length,
      active: take[1][0].nudge_schedule.nudge.active,
    })),
    leastUsed: nudgeTakes
      .slice(nudgeTakes.length - 3, nudgeTakes.length + 1)
      .map((take) => ({
        label: take[0],
        domain: take[1][0].nudge_schedule.nudge.domain as NudgeDomain,
        count: take[1].length,
        active: take[1][0].nudge_schedule.nudge.active,
      })),
  }
  return newTechniqueProfileData
}

function getClientProgress(practitioner: PractitionerFragment) {
  const currentMonth = dayjs()
    .subtract(dayjs().date() - 1, "days")
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
  const nextMonth = currentMonth.add(1, "month")
  const oneMonthAgo = currentMonth.subtract(1, "month")
  const twoMonthsAgo = currentMonth.subtract(2, "months")
  const threeMonthsAgo = currentMonth.subtract(3, "months")
  const fourMonthsAgo = currentMonth.subtract(4, "months")
  const fiveMonthsAgo = currentMonth.subtract(5, "months")
  const sixMonthsAgo = currentMonth.subtract(6, "months")

  const months = {
    current: {
      improving: 0,
      stable: 0,
      declining: 0,
    },
    oneAgo: {
      improving: 0,
      stable: 0,
      declining: 0,
    },
    twoAgo: {
      improving: 0,
      stable: 0,
      declining: 0,
    },
    threeAgo: {
      improving: 0,
      stable: 0,
      declining: 0,
    },
    fourAgo: {
      improving: 0,
      stable: 0,
      declining: 0,
    },
    fiveAgo: {
      improving: 0,
      stable: 0,
      declining: 0,
    },
  }

  const clientMonthCounts = {
    current: 0,
    oneAgo: 0,
    twoAgo: 0,
    threeAgo: 0,
    fourAgo: 0,
    fiveAgo: 0,
  }

  const clients = practitioner.practitioner_invite_codes

  clientMonthCounts.current += getClientCountBeforeMonth(clients, nextMonth)
  clientMonthCounts.oneAgo += getClientCountBeforeMonth(clients, currentMonth)
  clientMonthCounts.twoAgo += getClientCountBeforeMonth(clients, oneMonthAgo)
  clientMonthCounts.threeAgo += getClientCountBeforeMonth(clients, twoMonthsAgo)
  clientMonthCounts.fourAgo += getClientCountBeforeMonth(
    clients,
    threeMonthsAgo
  )
  clientMonthCounts.fiveAgo += getClientCountBeforeMonth(clients, fourMonthsAgo)

  clients.forEach((code) => {
    const user = code.user
    if (!user) {
      return
    }
    const currentMonthScore = getHighScore(
      user.questionnaire_takes,
      currentMonth,
      nextMonth
    )
    const oneMonthAgoScore = getHighScore(
      user.questionnaire_takes,
      oneMonthAgo,
      currentMonth
    )
    const twoMonthsAgoScore = getHighScore(
      user.questionnaire_takes,
      twoMonthsAgo,
      oneMonthAgo
    )
    const threeMonthsAgoScore = getHighScore(
      user.questionnaire_takes,
      threeMonthsAgo,
      twoMonthsAgo
    )
    const fourMonthsAgoScore = getHighScore(
      user.questionnaire_takes,
      fourMonthsAgo,
      threeMonthsAgo
    )
    const fiveMonthsAgoScore = getHighScore(
      user.questionnaire_takes,
      fiveMonthsAgo,
      fourMonthsAgo
    )
    const sixMonthsAgoScore = getHighScore(
      user.questionnaire_takes,
      sixMonthsAgo,
      fiveMonthsAgo
    )
    months.fiveAgo = iterateProgress(
      months.fiveAgo,
      fiveMonthsAgoScore,
      sixMonthsAgoScore
    )
    months.fourAgo = iterateProgress(
      months.fourAgo,
      fourMonthsAgoScore,
      fiveMonthsAgoScore
    )
    months.threeAgo = iterateProgress(
      months.threeAgo,
      threeMonthsAgoScore,
      fourMonthsAgoScore
    )
    months.twoAgo = iterateProgress(
      months.twoAgo,
      twoMonthsAgoScore,
      threeMonthsAgoScore
    )
    months.oneAgo = iterateProgress(
      months.oneAgo,
      oneMonthAgoScore,
      twoMonthsAgoScore
    )
    months.current = iterateProgress(
      months.current,
      currentMonthScore,
      oneMonthAgoScore
    )
  })

  months.current = getMonthPercentages(
    months.current,
    clientMonthCounts.current
  )
  months.oneAgo = getMonthPercentages(months.oneAgo, clientMonthCounts.current)
  months.twoAgo = getMonthPercentages(months.twoAgo, clientMonthCounts.current)
  months.threeAgo = getMonthPercentages(
    months.threeAgo,
    clientMonthCounts.current
  )
  months.fourAgo = getMonthPercentages(
    months.fourAgo,
    clientMonthCounts.current
  )
  months.fiveAgo = getMonthPercentages(
    months.fiveAgo,
    clientMonthCounts.current
  )

  const currentLabel = currentMonth.format("MMM")
  const oneAgoLabel = oneMonthAgo.format("MMM")
  const twoAgoLabel = twoMonthsAgo.format("MMM")
  const threeAgoLabel = threeMonthsAgo.format("MMM")
  const fourAgoLabel = fourMonthsAgo.format("MMM")
  const fiveAgoLabel = fiveMonthsAgo.format("MMM")

  return [
    [
      { x: fiveAgoLabel, y: months.fiveAgo.improving },
      { x: fourAgoLabel, y: months.fourAgo.improving },
      { x: threeAgoLabel, y: months.threeAgo.improving },
      { x: twoAgoLabel, y: months.twoAgo.improving },
      { x: oneAgoLabel, y: months.oneAgo.improving },
      { x: currentLabel, y: months.current.improving },
    ],
    [
      { x: fiveAgoLabel, y: months.fiveAgo.stable },
      { x: fourAgoLabel, y: months.fourAgo.stable },
      { x: threeAgoLabel, y: months.threeAgo.stable },
      { x: twoAgoLabel, y: months.twoAgo.stable },
      { x: oneAgoLabel, y: months.oneAgo.stable },
      { x: currentLabel, y: months.current.stable },
    ],
    [
      { x: fiveAgoLabel, y: months.fiveAgo.declining },
      { x: fourAgoLabel, y: months.fourAgo.declining },
      { x: threeAgoLabel, y: months.threeAgo.declining },
      { x: twoAgoLabel, y: months.twoAgo.declining },
      { x: oneAgoLabel, y: months.oneAgo.declining },
      { x: currentLabel, y: months.current.declining },
    ],
  ]
}

export function usePractitioner(id: string) {
  const graphqlClient = useGraphQLClient()
  const practitionerData = useGetPractitionerDataQuery(graphqlClient, { id })
  const { staffTableData } = useOrganization()
  const [clientProgress, setClientProgress] = useState<
    ClientProgressData | undefined
  >(undefined)
  const [techniqueProfile, setTechniqueProfile] = useState<
    TechniqueProfileData | undefined
  >(undefined)
  const [overview, setOverview] = useState<StaffOverviewData | undefined>(
    undefined
  )

  const practitioner = practitionerData?.data?.users_by_pk

  useEffect(() => {
    if (!practitioner) {
      return
    }
    const newClientProgress = getClientProgress(practitioner)
    setClientProgress(newClientProgress)
    const newTechniqueProfile = getTechniqueProfile(practitioner)
    setTechniqueProfile(newTechniqueProfile)
    const newOverview = getOverview(staffTableData ?? [], id)
    setOverview(newOverview)
  }, [id, practitioner, staffTableData])

  return {
    practitioner,
    clientProgress,
    techniqueProfile,
    overview,
  }
}
